import React from 'react'
import { IconRegistryItem } from '@microservices/wiskey-react-components'
import * as MuiIcons from '@mui/icons-material'

type IconComponentProps = {
  size?: number
  color?: string
}

export function createMuiWrapper(
  MuiIconComponent: React.ElementType<any>
): React.ComponentType<IconComponentProps> {
  return function MuiIconWrapper({ size = 24, color }: IconComponentProps) {
    const Comp = MuiIconComponent as any

    return React.createElement(Comp, {
      sx: {
        width: size,
        height: size,
        fontSize: size,
        color: color ?? 'inherit',
      },
      fontSize: 'inherit',
    })
  }
}

const russianTranslations: Record<string, string> = {
  home: 'дом',
  account: 'пользователь',
  user: 'пользователь',
  settings: 'настройки',
  search: 'поиск',
  edit: 'редактировать',
  delete: 'удалить',
  save: 'сохранить',
  add: 'добавить',
  close: 'закрыть',
  flight: 'самолет',
  airplane: 'самолет',
  plane: 'самолет',
  takeoff: 'взлетная полоса',
  landing: 'посадка',
  departure: 'вылет',
  arrival: 'прилет',
  airport: 'терминал',
  runway: 'взлетная полоса',
  baggage: 'багаж',
  luggage: 'багаж',
  suitcase: 'багаж',
  backpack: 'багаж',
  bus: 'автобус',
  taxi: 'такси',
  terminal: 'терминал',
  cafe: 'кафе',
  restaurant: 'ресторан',
}

const categoryKeywords: Record<string, string[]> = {
  AircraftMaintenance: [
    'aircraft',
    'airplane',
    'plane',
    'flight',
    'engine',
    'maintenance',
    'fuel',
    'gear',
    'wrench',
    'tools',
    'mechanic',
    'engineering',
    'build',
    'construction',
    'precision',
  ],
  AirsideOperations: [
    'runway',
    'tower',
    'arrival',
    'departure',
    'takeoff',
    'landing',
    'airport',
    'terminal',
    'gate',
    'hangar',
    'jetway',
    'flight',
  ],
  PassengerProcessing: [
    'passport',
    'ticket',
    'boarding',
    'checkin',
    'check-in',
    'passenger',
    'customer',
    'seat',
    'badge',
    'registration',
    'howtoreg',
    'confirmation',
    'member',
    'membership',
  ],
  GroundTransport: [
    'taxi',
    'bus',
    'shuttle',
    'parking',
    'train',
    'tram',
    'vehicle',
    'car',
    'directions',
    'route',
  ],
  TerminalServices: [
    'terminal',
    'info',
    'restroom',
    'toilet',
    'elevator',
    'escalator',
    'kiosk',
    'wifi',
    'charging',
    'power',
    'outlet',
  ],
  SafetySecurity: [
    'safety',
    'security',
    'shield',
    'lock',
    'alert',
    'warning',
    'fire',
    'emergency',
    'health',
  ],
  NavigationWeather: [
    'compass',
    'navigation',
    'map',
    'radar',
    'weather',
    'wind',
    'cloud',
    'sun',
    'location',
  ],
  LogisticsSupport: [
    'baggage',
    'luggage',
    'suitcase',
    'cargo',
    'belt',
    'trolley',
    'warehouse',
    'container',
    'shipping',
    'package',
    'delivery',
    'inventory',
    'parcel',
    'post',
    'mail',
  ],
  HospitalityRetail: [
    'restaurant',
    'cafe',
    'coffee',
    'bar',
    'shop',
    'store',
    'lounge',
    'hotel',
    'gift',
    'food',
    'meal',
    'shopping',
  ],
  Other: [],
}

function getCategoryFromName(iconName: string): string {
  const lower = iconName.toLowerCase()
  for (const [category, keywords] of Object.entries(categoryKeywords)) {
    if (keywords.some(kw => lower.includes(kw))) {
      if (category === 'GroundTransport' && lower.includes('cargo')) continue

      return category
    }
  }

  return 'Other'
}

function getRussianTranslation(iconName: string): string {
  const lower = iconName.toLowerCase()
  for (const [en, ru] of Object.entries(russianTranslations)) {
    if (lower.includes(en)) return ru
  }

  return ''
}

const MUI_EXCLUDE = new Set(['createSvgIcon', 'default', 'SvgIcon'])

function buildIconRegistry(): IconRegistryItem[] {
  const registry: IconRegistryItem[] = []

  // MaterialIcons (Google) - from @mui/icons-material
  for (const [exportName, IconComponent] of Object.entries(MuiIcons)) {
    if (!IconComponent) continue
    if (MUI_EXCLUDE.has(exportName)) continue
    if (exportName.startsWith('_')) continue
    const name = exportName
    registry.push({
      Component: createMuiWrapper(IconComponent as any),
      id: name,
      library: 'MaterialIcons',
      categories: [getCategoryFromName(name)],
      searchTerms: [name, getRussianTranslation(name)].filter(Boolean),
    })
  }

  return registry
}

export const ICON_REGISTRY = buildIconRegistry()
